import { useEffect, useMemo, useState } from "react";
import { getEvents, getKpis } from "./data/api.js";
import { PLATFORM_KEYS } from "./data/mock.js";
import Titlebar from "./components/Titlebar.jsx";
import Rail from "./components/Rail.jsx";
import Kpis from "./components/Kpis.jsx";
import Filters from "./components/Filters.jsx";
import EventsTable from "./components/EventsTable.jsx";
import Inspector from "./components/Inspector.jsx";
import StatusBar from "./components/StatusBar.jsx";
import IntegrationsPage from "./components/IntegrationsPage.jsx";

const ENABLED_DEFAULT = { er:true, skid:true, dice:true, fats:true, ra:true, evbr:false, tm:false };

function readRoute() {
  const h = (typeof location !== "undefined" && location.hash) || "";
  const r = h.replace(/^#/, "").trim();
  return r || "events";
}

export default function App() {
  const [route, setRoute] = useState(readRoute());
  const [events, setEvents] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState("all");
  const [view, setView] = useState("table");
  const [plats, setPlats] = useState(ENABLED_DEFAULT);
  const [q, setQ] = useState("");

  useEffect(() => {
    getEvents().then((evs) => {
      setEvents(evs);
      const goat = evs.find((e) => e.code === "GO");
      if (goat) setSelectedId(goat.id);
      else if (evs[0]) setSelectedId(evs[0].id);
    }).catch(()=>{});
    getKpis().then(setKpis).catch(()=>{});
  }, []);

  // sync hash routing
  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if (typeof location !== "undefined") location.hash = route;
  }, [route]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return events.filter((e) => {
      if (status === "live"     && !["live","sold","alert"].includes(e.status)) return false;
      if (status === "upcoming" && e.status !== "soon")  return false;
      if (status === "draft"    && e.status !== "draft") return false;
      const anyEnabled = PLATFORM_KEYS.some((k) => plats[k] && (e.sales[k] > 0 || e.status === "draft"));
      if (!anyEnabled) return false;
      if (term) {
        const hay = `${e.title} ${e.sub} ${e.venue} ${e.city} ${e.id}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [events, status, plats, q]);

  const selected = events.find((e) => e.id === selectedId) || null;
  const togglePlat = (k) => setPlats((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="app">
      <Titlebar/>
      <Rail route={route} onRoute={setRoute}/>
      <main className="main">
        {route === "events" ? (
          <EventsView
            events={events} filtered={filtered} kpis={kpis}
            selected={selected} setSelectedId={setSelectedId}
            status={status} setStatus={setStatus}
            view={view} setView={setView}
            plats={plats} togglePlat={togglePlat}
            q={q} setQ={setQ}
          />
        ) : route === "integrations" ? (
          <IntegrationsPage/>
        ) : (
          <Stub label={route}/>
        )}
        <StatusBar/>
      </main>
    </div>
  );
}

function EventsView(p) {
  return (
    <>
      <div className="pageh">
        <div>
          <h1>Events <em>— all platforms</em></h1>
          <div className="sub">
            Showing <b>{p.filtered.length}</b> of <b>{p.events.length}</b> events across <b>7</b> connected channels · last sync <b>02:14</b> ago · <b>Apr–Aug 2026</b>
          </div>
        </div>
        <div className="pageh-actions">
          <button className="btn">Export CSV</button>
          <button className="btn">Bulk update</button>
          <button className="btn primary">+ New event <span className="kbd">N</span></button>
        </div>
      </div>
      <Kpis data={p.kpis}/>
      <Filters
        status={p.status} onStatus={p.setStatus}
        view={p.view} onView={p.setView}
        plats={p.plats} togglePlat={p.togglePlat}
        q={p.q} onQ={p.setQ}
      />
      <div className="body">
        <EventsTable events={p.filtered} selectedId={p.selected?.id} onSelect={p.setSelectedId}/>
        <Inspector ev={p.selected}/>
      </div>
    </>
  );
}

function Stub({ label }) {
  const titles = {
    sales: "Sales", audience: "Audience", inventory: "Inventory",
    door: "Door & Scan", payouts: "Payouts", fees: "Fees & Splits",
    distribution: "Distribution", insights: "Insights", settings: "Settings",
  };
  return (
    <div className="pageh">
      <div>
        <h1>{titles[label] || label} <em>— coming next</em></h1>
        <div className="sub">This module is on the roadmap. The Events + Integrations modules are live.</div>
      </div>
    </div>
  );
}
